'use client';

import { c as _c } from "react/compiler-runtime";
import { PropsWithChildren, createContext, use, useContext } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useIsPageVisible } from '@/hooks/useIsPageVisible';
import { useTotalVramKb } from '@/hooks/useTotalVramKb';
import type { UASnapshot } from '@/server/lib/ua-snapshot';
export type DefaultDeviceVramTitle = UASnapshot['defaultDeviceVramTitle'];
type UAContextType = {
  isAndroidMobile: boolean;
  isIosMobileApp: boolean;
  isMobileApp: boolean;
  isPersonaApp: boolean;
  mobileAppVersion: string | null;
  defaultDeviceVramTitle: DefaultDeviceVramTitle;
  device: UASnapshot['device'];
  deviceType: UASnapshot['deviceType'];
  deviceTypeWithAndroidAndIOS: UASnapshot['deviceTypeWithAndroidAndIOS'];
  totalVramKb?: number;
  uaInfo: UASnapshot['uaInfo'];
  isMobileLandscape: boolean;
  isMobile: boolean;
  isHoverSupported: boolean;
  isCoarsePointer: boolean;
  isPageVisible: boolean;
};
type UAProviderProps = PropsWithChildren<{
  snapshotPromise: Promise<UASnapshot>;
}>;
const UAContext = createContext<UAContextType | null>(null);
export function UAProvider(t0) {
  const $ = _c(26);
  const {
    children,
    snapshotPromise
  } = t0;
  const {
    device,
    deviceType,
    deviceTypeWithAndroidAndIOS,
    defaultDeviceVramTitle,
    isAndroidMobile,
    isIosMobileApp,
    isMobileApp,
    isPersonaApp,
    mobileAppVersion,
    uaInfo
  } = use(snapshotPromise);
  const totalVramKb = useTotalVramKb(deviceType);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initializeWithValue: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isMobileLandscape = useMediaQuery("(orientation: landscape) and (max-height: 480px)", t1);
  const ssrIsMobile = device === "mobile_browser" || device === "mobile_app";
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      initializeWithValue: false
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const cssIsMobile = useMediaQuery("(max-width: 767px) or (max-height: 480px)", t2);
  const isMobile = ssrIsMobile || cssIsMobile;
  const t3 = device === "mobile_browser" || device === "mobile_app";
  let t4;
  if ($[2] !== t3) {
    t4 = {
      defaultValue: t3,
      initializeWithValue: false
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isCoarsePointer = useMediaQuery("(pointer: coarse)", t4);
  const t5 = device === "desktop_browser";
  let t6;
  if ($[4] !== t5) {
    t6 = {
      defaultValue: t5,
      initializeWithValue: false
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const isHoverSupported = useMediaQuery("(hover: hover) and (pointer: fine)", t6);
  const isPageVisible = useIsPageVisible();
  let t7;
  if ($[6] !== defaultDeviceVramTitle || $[7] !== device || $[8] !== deviceType || $[9] !== deviceTypeWithAndroidAndIOS || $[10] !== isAndroidMobile || $[11] !== isCoarsePointer || $[12] !== isHoverSupported || $[13] !== isIosMobileApp || $[14] !== isMobile || $[15] !== isMobileApp || $[16] !== isMobileLandscape || $[17] !== isPageVisible || $[18] !== isPersonaApp || $[19] !== mobileAppVersion || $[20] !== totalVramKb || $[21] !== uaInfo) {
    t7 = {
      isAndroidMobile,
      isIosMobileApp,
      isMobileApp,
      isPersonaApp,
      mobileAppVersion,
      defaultDeviceVramTitle,
      device,
      deviceType,
      deviceTypeWithAndroidAndIOS,
      totalVramKb,
      uaInfo,
      isMobileLandscape,
      isMobile,
      isHoverSupported,
      isCoarsePointer,
      isPageVisible
    };
    $[6] = defaultDeviceVramTitle;
    $[7] = device;
    $[8] = deviceType;
    $[9] = deviceTypeWithAndroidAndIOS;
    $[10] = isAndroidMobile;
    $[11] = isCoarsePointer;
    $[12] = isHoverSupported;
    $[13] = isIosMobileApp;
    $[14] = isMobile;
    $[15] = isMobileApp;
    $[16] = isMobileLandscape;
    $[17] = isPageVisible;
    $[18] = isPersonaApp;
    $[19] = mobileAppVersion;
    $[20] = totalVramKb;
    $[21] = uaInfo;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== children || $[24] !== t7) {
    t8 = <UAContext.Provider value={t7}>{children}</UAContext.Provider>;
    $[23] = children;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
export const useUAContext = () => {
  const value = useContext(UAContext);
  if (value == null) {
    throw new Error("useUAContext cannot be used outside of UAProvider");
  }
  return value;
};